import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { Track } from 'livekit-client';
import {
  useVoiceAssistant,
  useRoomContext,
  useRemoteParticipants,
  useTracks,
  VideoTrack,
} from '@livekit/components-react';
import type { AppConfig } from '../types/types';
import type { Theme } from '../types/theme';
import { cn } from '../utils/utils';
import { ChatEntry } from './voice_components/chat-entry';
import { AgentControlBar } from './voice_components/agent-control-bar';
import { CircularVisualizer } from './voice_components/circular-visualizer';
import useChatAndTranscription from '../hooks/useChatAndTranscription';
import { useDebugMode } from '../hooks/useDebugMode';
import { useLocalTrackRef } from '../hooks/useLocalTrackRef';
import { BlobShape } from './voice_components/ui/Blobshape';

interface VoiceAgentProps {
  appConfig: AppConfig;
  disabled: boolean;
  sessionStarted: boolean;
  isWidget?: boolean;
  onDisconnect?: () => void;
  theme?: Theme;
}

export const VoiceAgent = ({
  appConfig,
  disabled,
  sessionStarted = true,
  isWidget = false,
  onDisconnect,
  theme,
  ref,
}: React.ComponentProps<'div'> & VoiceAgentProps) => {
  const { state: agentState, audioTrack: agentAudioTrack, videoTrack: agentVideoTrack } = useVoiceAssistant();
  const [chatOpen, setChatOpen] = useState(false);
  const { messages, send } = useChatAndTranscription(chatOpen);
  const room = useRoomContext();
  const participants = useRemoteParticipants();
  const [isConnected, setIsConnected] = useState(false);

  // Video tracks
  const [screenShareTrack] = useTracks([Track.Source.ScreenShare]);
  const cameraTrack = useLocalTrackRef(Track.Source.Camera);

  useDebugMode({
    enabled: process.env.NODE_ENV !== 'production',
  });

  async function handleSendMessage(message: string) {
    if (!message.trim()) {
      return;
    }
    try {
      await send(message);
    } catch (error) {
      console.error('❌ Error sending message:', error);
      throw error;
    }
  }

  const handleDisconnectClick = () => {
    onDisconnect?.();
  };

  useEffect(() => {
    if (!sessionStarted) return;
    const hasAgent = participants.some((p) => p.isAgent);
  }, [participants, agentState, sessionStarted]);

  useEffect(() => {
    const isRoomConnected = room.state === 'connected';
    setIsConnected(isRoomConnected);
  }, [participants, agentState, room.state]);

  const { supportsChatInput, supportsVideoInput, supportsScreenShare } = appConfig;
  const capabilities = { supportsChatInput, supportsVideoInput, supportsScreenShare };
  const shouldShow = isConnected || sessionStarted;
  const lastMessage = messages[messages.length - 1];

  // Check for video modes
  const isCameraEnabled = cameraTrack && !cameraTrack.publication.isMuted;
  const isScreenShareEnabled = screenShareTrack && !screenShareTrack.publication.isMuted;
  const isAvatar = agentVideoTrack !== undefined;
  const hasVideo = isCameraEnabled || isScreenShareEnabled || isAvatar;

  // State text display
  const getStateDisplay = () => {
    switch (agentState) {
      case 'speaking':
        return 'Speaking...';
      case 'thinking':
        return 'Thinking...';
      case 'listening':
      default:
        return 'Listening...';
    }
  };

  const stateText = getStateDisplay();
  // Responsive orb size - smaller on mobile, bigger when chat is open
  const orbSize = chatOpen ? Math.max(240, Math.min(480, Math.min(window.innerWidth, window.innerHeight) * 0.4)) : Math.max(180, Math.min(400, Math.min(window.innerWidth, window.innerHeight) * 0.35));

  // Use theme colors or fallback defaults
  const textColor = theme?.textColor ?? '#f9fafb';
  const primaryColor = theme?.primaryColor ?? '#f97316'; // Default orange

  // Determine whether the voice session is active (connected or session started)
  const voiceActive = isConnected || sessionStarted;

  // Prepare CSS custom properties so the background can react to the app theme (light/dark)
  // Determine dark mode: prefer provided `theme` prop, otherwise check document root class
  const isDark = theme?.mode === 'dark' ? true : (typeof document !== 'undefined' ? document.documentElement.classList.contains('dark') : false);


  return (
    <section
      ref={ref}
      className={cn(
        'relative flex flex-col items-center justify-between h-full w-full transition-opacity duration-300 py-2 sm:py-4 overflow-hidden voice-bg',
        shouldShow ? 'opacity-100' : 'opacity-0',
        voiceActive ? 'voice-active' : ''
      )}
    >
      <BlobShape color="purple" position={{ top: "20%", right: "10%" }} size="400px" isDark={isDark} />
      <BlobShape color="cyan" position={{ bottom: "20%", left: "10%" }} size="500px" delay={1} isDark={isDark} />
      {/* Main content area */}
      <div className="flex flex-col items-center justify-center flex-1 w-full px-4">
        {/* Video fullscreen when enabled and chat closed */}
        {hasVideo && !chatOpen ? (
          <div className="w-full h-full">
            <AnimatePresence mode="popLayout">
              {isAvatar && (
                <motion.div
                  key="avatar-fullscreen"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.3 }}
                  className="h-full w-full"
                >
                  <VideoTrack
                    trackRef={agentVideoTrack}
                    width={agentVideoTrack?.publication?.dimensions?.width ?? 0}
                    height={agentVideoTrack?.publication?.dimensions?.height ?? 0}
                    className="h-full w-full object-cover"
                  />
                </motion.div>
              )}
              {isCameraEnabled && (
                <motion.div
                  key="camera-fullscreen"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.3 }}
                  className="h-full w-full"
                >
                  <VideoTrack
                    trackRef={cameraTrack}
                    width={cameraTrack?.publication?.dimensions?.width ?? 0}
                    height={cameraTrack?.publication?.dimensions?.height ?? 0}
                    className="h-full w-full object-cover"
                  />
                </motion.div>
              )}
              {isScreenShareEnabled && (
                <motion.div
                  key="screen-fullscreen"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.3 }}
                  className="h-full w-full"
                >
                  <VideoTrack
                    trackRef={screenShareTrack}
                    width={screenShareTrack?.publication?.dimensions?.width ?? 0}
                    height={screenShareTrack?.publication?.dimensions?.height ?? 0}
                    className="h-full w-full object-contain"
                  />
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        ) : (
          <>
            {/* STATE TEXT - shown below orb */}
            {stateText && (
              <div className="-mt-4">
                <span
                  className="text-xs sm:text-sm font-medium animate-pulse"
                  style={{ color: textColor }}
                >
                  {stateText}
                </span>
              </div>
            )}

            <div className="flex flex-col items-center w-full max-w-full">

              {/* ORB - responsive with extra padding for shake animation */}
              <div
                className="relative overflow-hidden rounded-full"
                style={{
                  width: '90%',
                  maxWidth: orbSize,
                  aspectRatio: '1 / 1',
                }}
              >
                <CircularVisualizer
                  trackRef={agentAudioTrack}
                  className="w-full h-full"
                  radius={chatOpen ? 90 : 90}
                  agentState={agentState}
                  primaryColor={primaryColor}
                />
              </div>


              {/* Transcription - shown when chatOpen, positioned below state text */}
              {chatOpen && lastMessage && (
                <div className="w-full mt-2 max-w-md px-2 notranslate">
                  <AnimatePresence mode="wait">
                    <motion.div
                      key={lastMessage.id}
                      initial={{ opacity: 0, translateY: 10 }}
                      animate={{ opacity: 1, translateY: 0 }}
                      exit={{ opacity: 0, translateY: -10 }}
                      transition={{ duration: 0.3 }}
                      className="w-full"
                    >
                      {/* Streaming text container - Max 2 lines (h-20 allowed to start fade), shows bottom-most text */}
                      <div
                        className="relative h-20 w-full overflow-hidden flex flex-col justify-end"
                        style={{
                          maskImage: 'linear-gradient(to bottom, transparent 0%, black 40%)',
                          WebkitMaskImage: 'linear-gradient(to bottom, transparent 0%, black 40%)'
                        }}
                      >
                        <div
                          className="text-sm font-medium whitespace-pre-wrap leading-relaxed text-center"
                          style={{
                            color: textColor,
                            opacity: 0.9,
                          }}
                        >
                          <ChatEntry entry={lastMessage} />
                        </div>
                      </div>
                    </motion.div>
                  </AnimatePresence>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* Control Bar at bottom - fixed height */}
      <motion.div
        key="control-bar"
        initial={{ opacity: 0, translateY: 20 }}
        animate={{
          opacity: sessionStarted ? 1 : 0,
          translateY: sessionStarted ? 0 : 20,
        }}
        transition={{ duration: 0.4, delay: sessionStarted ? 0.2 : 0, ease: 'easeOut' }}
        className="w-full max-w-2xl px-4 flex-shrink-0"
      >
        <AgentControlBar
          capabilities={capabilities}
          onChatOpenChange={setChatOpen}
          onSendMessage={handleSendMessage}
          onDisconnect={handleDisconnectClick}
          isWidget={isWidget}
        />
      </motion.div>
    </section>
  );
};